import type { Action, ThunkDispatch } from '@reduxjs/toolkit';
import { createAsyncThunk, unwrapResult } from '@reduxjs/toolkit';
import type { TimelyPresentableAiPrediction } from '@timelyapp/integration-cloud-api-spec';
import { hourDraftFactory } from 'factories/hours';
import { maxBy } from 'lodash-es';
import { bulkSaveHourDraftsThenReload } from 'redux/modules/hours.actions';
import { setGeneratedNotes } from 'redux/modules/hours.types';
import { fetchFullProjectsIfNeeded } from 'redux/modules/projects.actions';
import { showToast } from 'redux/modules/toasts.actions';
import { getHourEntities } from 'redux/selectors/hours';
import { getHourDraft } from 'redux/selectors/hours.draft.base';
import { getProjectVariantMap } from 'redux/selectors/projects';
import { getUserId } from 'redux/selectors/user';
import { getFullProjectOrNull } from 'redux/selectors/utils/projects';
import type { IState } from 'redux/state';
import timelyAnalytics from 'tracking/timely-analytics';
import type { HourAnalyticsContext } from 'types/analytics';
import type { Hour, HourDraft } from 'types/hour';
import type { ProjectVariantMap } from 'types/project';
import type { Thunk } from 'types/redux';
import debug from 'utils/debug';
import { calculateDuration } from 'utils/durations';
import { constructTimestampsContainingTicMemoriesForHourIfMissing } from 'utils/hours';
import { pluralize } from 'utils/text-helper';
import { calculateDurationFromTimestamps, mutateTimestampRange } from 'utils/timestamps';
import { getDemoMemoriesData } from '~/redux/modules/tic/tic.memories.synthetic.selectors';
import { getConsolidatedPredictedIntervalsWithMeta } from './predictions.selectors';

/**
 * Maps a single AI prediction to an HourDraft, handling both new entries and merges with existing hours.
 * Pure function extracted for testability.
 */
export function mapPredictionToHourDraft(
  prediction: TimelyPresentableAiPrediction,
  projectVariantMap: ProjectVariantMap,
  hoursMap: Record<number, Hour<number> | undefined>,
  currentUserId: number,
  analyticsContext: HourAnalyticsContext
): HourDraft | undefined {
  if (!prediction.valid_as_hour) {
    return undefined;
  }

  const existingHour = !!prediction.existing_hour_id && hoursMap[prediction.existing_hour_id];
  const timestampsAsIso = prediction.timestamps.map(ts => ({
    from: ts.started_at.toISO(),
    to: ts.ended_at.toISO(),
    entry_ids: [], // TODO: Revisit to ensure we didn't miss a use-case
  }));

  // Matches existing time entry, merge with prediction
  if (existingHour) {
    // In case `hour.timestamps` is missing (but it has duration), we must try
    // to create the timestamps[] (using the duration + `updated_at` as a base)
    const existingTimestamps =
      constructTimestampsContainingTicMemoriesForHourIfMissing(existingHour);
    const timestamps = mutateTimestampRange('add', timestampsAsIso, existingTimestamps);
    const duration = calculateDurationFromTimestamps(timestamps);

    const existingProjectVariant = existingHour.project
      ? projectVariantMap[existingHour.project]
      : null;
    const existingFullProject = getFullProjectOrNull(existingProjectVariant);
    const isBillable = existingFullProject?.billable ?? existingHour.billable;

    return hourDraftFactory(
      {
        ...existingHour,
        duration,
        seconds: duration.total_seconds,
        timestamps,
        billable: isBillable,
      },
      analyticsContext
    );
  }

  // Make a new time entry, given a valid project ID
  const duration = calculateDurationFromTimestamps(timestampsAsIso);

  // Look up project's billable status from state.projects.projectById
  // getFullProjectOrNull returns the full Project if variant.type === 'full', else null
  const projectVariant = prediction.project_id ? projectVariantMap[prediction.project_id] : null;
  const fullProject = getFullProjectOrNull(projectVariant);
  const isBillable = fullProject?.billable ?? false;

  return hourDraftFactory(
    {
      day: prediction.date,
      note: prediction.note || undefined,
      project: prediction.project_id,
      label_ids: prediction.label_ids,
      seconds: duration.total_seconds,
      duration,
      timestamps: timestampsAsIso,
      user: { id: currentUserId },
      billable: isBillable,
    },
    analyticsContext
  );
}

/**
 * Turn AI predictions into time entries for the active date range
 */
export const acceptAiPredictionsAsHours = createAsyncThunk<
  void,
  {
    /**
     * YYYY-MM-DD/s we want to accept predictions for.
     * @todo, required for adding week view suport.
     */
    date?: string | string[];
  },
  { state: IState; dispatch: ThunkDispatch<IState, any, Action> }
>('tic/ai/acceptAiPredictionsAsHours', async (params, thunkAPI) => {
  debug.template.ai('Accepting all predictions..', { subtopic: 'Predictions' });
  const state = thunkAPI.getState() as IState;
  const currentUserId = getUserId(state);
  const hoursMap = getHourEntities(state);
  // Project map lives at state.projects.projectById - contains ProjectVariant objects
  let projectVariantMap = getProjectVariantMap(state);
  const { predictions, meta } = getConsolidatedPredictedIntervalsWithMeta(state);

  // Ensure full project data is available before processing predictions
  const predictionProjectIds = [
    ...new Set(predictions.map(p => p.project_id).filter((id): id is number => id != null)),
  ];

  if (predictionProjectIds.length > 0) {
    await thunkAPI.dispatch(fetchFullProjectsIfNeeded(predictionProjectIds));
    projectVariantMap = getProjectVariantMap(thunkAPI.getState());
  }

  const totalDuration = calculateDuration(meta.totalSeconds);
  timelyAnalytics.trackTic('TIC+AI Click Magic AI Button', {
    suggestion_count: predictions.length,
    total_duration: `${totalDuration.hours}h ${totalDuration.minutes}m`,
  });
  const analyticsContext = { interaction: 'TIC+AI Click Magic AI Button' } as const;

  const subtopic = 'Magic Button'; // For logging
  let numInvalidAsHourPredictions = 0;

  // Prepare create/update payloads for time entries
  const hourDrafts = predictions
    .map(prediction => {
      if (!prediction.valid_as_hour) {
        numInvalidAsHourPredictions++;
      }
      return mapPredictionToHourDraft(
        prediction,
        projectVariantMap,
        hoursMap,
        currentUserId,
        analyticsContext
      );
    })
    .filter(h => h !== undefined);

  if (!hourDrafts.length) {
    debug.template.ai('No changes to save, exiting early.', { hourDrafts, subtopic });
    return;
  }

  // Save & reload the time entries
  await thunkAPI
    .dispatch(bulkSaveHourDraftsThenReload(hourDrafts))
    .then(unwrapResult)
    .then(response => {
      const numCreated = response.created_ids?.length || 0;
      const numUpdated = response.updated_ids?.length || 0;
      const numCreateErrors = response.errors?.create?.length || 0;
      const numUpdateErrors = response.errors?.update?.length || 0;
      const numErrors = numCreateErrors + numUpdateErrors;

      if (numErrors) {
        thunkAPI.dispatch(
          showToast({
            title: `${pluralize(numErrors, {
              singular: 'suggestion',
              plural: 'suggestions',
            })} could not be saved`,
            message: 'Try manually creating or updating remaining suggestions.',
            type: 'danger',
            dismissAfter: 3_000,
          })
        );
      }

      if (numInvalidAsHourPredictions) {
        thunkAPI.dispatch(
          showToast({
            title: `${pluralize(numInvalidAsHourPredictions, {
              singular: 'suggestion',
              plural: 'suggestions',
            })} were skipped`,
            message: 'Try logging remaining suggestions manually',
            type: 'danger',
            dismissAfter: 3_000,
          })
        );
      }

      debug.template.ai(
        `Created ${numCreated}, updated ${numUpdated} time entries. ${numErrors} errors. ${numInvalidAsHourPredictions} were invalid`,
        { hourDrafts, predictions, response, subtopic }
      );
      return response;
    })
    .catch(error => {
      debug.template.ai('Failed to save time entries', { error, subtopic });
    });
});

export const generateSyntheticNoteForHour = (): Thunk<Promise<void>> => {
  return async (dispatch, getState) => {
    const state = getState();
    const hourDraft = getHourDraft(state);
    const blocks = getDemoMemoriesData(state).blocks;

    const minuteIndex = (timestamp: string): number =>
      Math.floor(new Date(timestamp).getTime() / 60000);

    const minuteIndexToBlockIndexMap = blocks.reduce(
      (acc: Record<number, number>, block, index) => {
        const startIndex = minuteIndex(block.from);
        const endIndex = minuteIndex(block.to);
        for (let i = startIndex; i <= endIndex; i++) {
          acc[i] = index;
        }
        return acc;
      },
      {}
    );
    const blockIndexScoreMap: Record<number, number> = {};
    hourDraft?.timestamps.forEach(ts => {
      const startIndex = minuteIndex(ts.from);
      const endIndex = minuteIndex(ts.to);
      for (let i = startIndex; i <= endIndex; i++) {
        const blockIndex = minuteIndexToBlockIndexMap[i];
        if (blockIndex !== undefined) {
          blockIndexScoreMap[blockIndex] = (blockIndexScoreMap[blockIndex] || 0) + 1;
        }
      }
    });

    const [highestScoringBlockIndex, score] = maxBy(
      Object.entries(blockIndexScoreMap),
      entry => entry[1]
    ) ?? [-1, 0];
    if (score < 5) {
      return;
    }

    await new Promise(resolve => setTimeout(resolve, 900));

    const block = blocks[Number(highestScoringBlockIndex)];
    if (!block) {
      return;
    }
    const notes = block.aiNotes;
    dispatch(
      setGeneratedNotes({
        with_reference: notes.detailed,
        without_reference: notes.concise,
      })
    );
  };
};
